import * as React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'

import { RootState } from '../../store/rootReducer'
import { User } from 'src/lib/interfaces/User.interface'
import { Location } from 'src/lib/interfaces/Location.interface'
import { UserAuthority } from 'src/lib/interfaces/UserAuthority.interface'

import { Table, Collapse } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Breadcrumbs from '../Breadcrumbs.component'

import * as adminActions from 'src/store/admin/actions'

import { formatName } from 'src/lib/helpers/format'

const map = require('lodash/map')
const some = require('lodash/some')
const sortBy = require('lodash/sortBy')

export interface UsersManagementPageProps extends RouteComponentProps {
	users?: User[]
	isFetchingUsers: boolean
	allLocations?: Location[]
	allRoles?: UserAuthority[]
	fetchUsersList: (next?: (err?: any, users?: User[]) => void) => void
	fetchAllRolesList: (next?: (err?: any, roles?: UserAuthority[]) => void) => void
	fetchAllLocationsList: (next?: (err?: any, locations?: Location[]) => void) => void
	toggleUserRole: (userId: number, role: string, toggle: boolean, next?: (err?: any, user?: User) => void) => void
	toggleUserLocation: (userId: number, locationId: number, toggle: boolean, next?: (err?: any, user?: User) => void) => void
}

export interface UsersManagementPageState {
  expandedUserIndex?: number
}

class UsersManagementPage extends React.PureComponent<UsersManagementPageProps, UsersManagementPageState> {
	state = {
		expandedUserIndex: undefined
	}

  componentDidMount() {
		this.props.fetchUsersList()
		this.props.fetchAllRolesList()
		this.props.fetchAllLocationsList()
	}

	renderBadge(title, highlighted, onClick) {
		return (
			<div
				onClick={onClick}
				style={{ fontSize: '12px', width: 'fit-content' }}
				className={classnames('badge cursor-pointer mb-2 px-2', { 'badge-primary': highlighted, 'badge-dark': !highlighted })}
			>
				{title}
			</div>
		)
	}
	
	renderExpandedUserContent() {
		const { expandedUserIndex } = this.state
		const { users, allRoles, allLocations } = this.props

		if (!users || expandedUserIndex === undefined) { return null }

		const user = users[expandedUserIndex!]
		if (!user) { return null }

		const sortedRoles = sortBy(allRoles, 'authority')
		const sortedLocations = sortBy(allLocations, 'code')

		return (
			<div className="p-3">
				<section className="row no-gutters">
					<div className="col-6 d-flex flex-column">
						<b>Locations</b>
						<div className="mt-3 d-flex flex-column pl-3">
							{map(sortedLocations, location => {
								const isFromUser = some(user.locations, { id: location.id })
								return this.renderBadge(location.code, isFromUser, () => {
									this.props.toggleUserLocation(user.id, location.id, !isFromUser)
								})
							})}
						</div>
					</div>

					<div className="col-6 d-flex flex-column">
						<b>Permissions</b>
						<div className="mt-3 d-flex flex-column pl-3">
							{map(sortedRoles, role => {
								const isFromUser = some(user.authorities, { authority: role.authority })
								return this.renderBadge(role.authority, isFromUser, () => {
									this.props.toggleUserRole(user.id, role.authority, !isFromUser)
								})
							})}
						</div>
					</div>
				</section>
			</div>
		)
	}

  render() {
		const { expandedUserIndex } = this.state
    const { users, isFetchingUsers } = this.props

    return (
      <div className="d-flex flex-column min-height-100">
        <Breadcrumbs breadcrumbs={[
          { name: 'Dashboard', link: '/dashboard' },
          { name: 'Users Management', active: true },
        ]} />

        <div className="page applications-page flex-grow-1 min-height-0">
          <section>
            <div className="d-flex align-items-center mb-2">
              <h5 className="title mb-0">Users Management</h5>
              {isFetchingUsers && <FontAwesomeIcon spin size="lg" className="ml-3" icon={['fas', 'spinner']} />}
            </div>

						<Table hover className="ggtable">
              <thead>
                <tr>
                  <th>Name</th>
									<th>Email</th>
                </tr>
              </thead>
              
              <tbody>
                {map(users, (user, index) => {
									const isRowExpanded = expandedUserIndex !== undefined && expandedUserIndex === index

									return (
										<React.Fragment>
											<tr
												key={user.id}
												onClick={() => this.setState({ expandedUserIndex: isRowExpanded ? undefined : index })}
											>
												<td>{formatName(user.employee)}</td>
												<td>
													<div className="d-flex align-items-center justify-content-between">
														{user.username}
														<FontAwesomeIcon
															icon={['fas', 'chevron-down']}
															className="ggmenu-collapsible-toggleicon"
															flip={isRowExpanded ? 'vertical' : undefined}
														/>
													</div>
												</td>
											</tr>

											<tr className="expandable" key={`${user.id}-expanded`}>
												<td colSpan={2} className="py-0">
													<Collapse isOpen={isRowExpanded}>
														<div style={{ padding: '0 40px' }}>
															<Table className="ggtable mt-3">
																<tbody>
																	{isRowExpanded &&
																		this.renderExpandedUserContent()
																	}
																</tbody>
															</Table>
														</div>
													</Collapse>
												</td>
											</tr>
										</React.Fragment>
									)
								})}
              </tbody>
            </Table>
          </section>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
	users: state.admin.users,
	allRoles: state.admin.allRoles,
	allLocations: state.admin.allLocations,
	isFetchingUsers: state.admin.isFetchingUsers,
})

const mapDispatchToProps = (dispatch) => ({
	fetchUsersList: (next?: (err?: any, users?: User[]) => void) => dispatch(adminActions.fetchUsersList(next)),
	fetchAllRolesList: (next?: (err?: any, roles?: UserAuthority[]) => void) =>
		dispatch(adminActions.fetchAllRolesList(next)),
	fetchAllLocationsList: (next?: (err?: any, locations?: Location[]) => void) =>
		dispatch(adminActions.fetchAllLocationsList(next)),
	toggleUserRole: (userId: number, role: string, toggle: boolean, next?: (err?: any, user?: User) => void) =>
		dispatch(adminActions.toggleUserRole(userId, role, toggle, next)),
	toggleUserLocation: (userId: number, locationId: number, toggle: boolean, next?: (err?: any, user?: User) => void) =>
		dispatch(adminActions.toggleUserLocation(userId, locationId, toggle, next))
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UsersManagementPage))


// WEBPACK FOOTER //
// ./src/routes/admin/UsersManagement.page.tsx